import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, Table, Thead, Tbody, Tr, Th, Td, Select,
  Alert, AlertIcon, AlertDescription, useToast, Tabs, TabList, TabPanels, Tab, TabPanel,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure, Checkbox,
  Input, Switch, IconButton, Tooltip,
} from '@chakra-ui/react';
import { FormControl, FormLabel } from '@chakra-ui/form-control';
import {
  FiRefreshCw, FiPlus, FiTrash2, FiEdit2, FiSave, FiPlay, FiSearch, FiDownload,
  FiCopy, FiChevronDown, FiChevronUp,
} from 'react-icons/fi';

const API_BASE = '/api/v1';
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Token ${localStorage.getItem('token')}`,
});

const CLOUDFORMATION_TEMPLATE = `AWSTemplateFormatVersion: '2010-09-09'
Description: >-
  S3 bucket for Selwyn Infrastructure Manager backups.
  Objects are automatically deleted after 30 days via a lifecycle rule.

Parameters:
  BucketName:
    Type: String
    Description: Name for the S3 backup bucket
    AllowedPattern: '[a-z0-9][a-z0-9.\\\\-]*[a-z0-9]'
    ConstraintDescription: >-
      Must be a valid S3 bucket name (lowercase letters, numbers, hyphens
      and periods; 3-63 characters).
    MinLength: 3
    MaxLength: 63

Resources:
  BackupBucket:
    Type: AWS::S3::Bucket
    Properties:
      BucketName: !Ref BucketName
      VersioningConfiguration:
        Status: Enabled
      LifecycleConfiguration:
        Rules:
          - Id: DeleteBackupsAfter30Days
            Status: Enabled
            ExpirationInDays: 30
      PublicAccessBlockConfiguration:
        BlockPublicAcls: true
        BlockPublicPolicy: true
        IgnorePublicAcls: true
        RestrictPublicBuckets: true
      BucketEncryption:
        ServerSideEncryptionConfiguration:
          - ServerSideEncryptionByDefault:
              SSEAlgorithm: AES256
      Tags:
        - Key: Purpose
          Value: selwyn-backups

Outputs:
  BucketArn:
    Description: ARN of the created backup bucket
    Value: !GetAtt BackupBucket.Arn
  BucketName:
    Description: Name of the created backup bucket
    Value: !Ref BackupBucket
`;

/* ────────────────────────────────
   S3 Config Panel
   ──────────────────────────────── */
function S3ConfigPanel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [form, setForm] = useState({
    bucket_name: '', region: 'us-east-1', access_key_id: '',
    secret_access_key: '', prefix: 'backups/', endpoint_url: '',
  });
  const toast = useToast();

  const fetchConfig = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/backup-s3-config/`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        const items = data.results || data;
        if (items.length > 0) {
          const c = items[0];
          setConfig(c);
          setForm({
            bucket_name: c.bucket_name || '',
            region: c.region || 'us-east-1',
            access_key_id: c.access_key_id || '',
            secret_access_key: '',
            prefix: c.prefix || 'backups/',
            endpoint_url: c.endpoint_url || '',
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const saveConfig = async () => {
    setSaving(true);
    const body = { ...form };
    if (!body.secret_access_key && config) delete body.secret_access_key;
    try {
      const res = await fetch(`${API_BASE}/backup-s3-config/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: 'S3 config saved', status: 'success', duration: 3000 });
      fetchConfig();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
      <Heading size="md" mb={4}>S3 Bucket Configuration</Heading>
      {config && <Badge colorScheme="green" mb={4}>Configured</Badge>}
      {!config && <Badge colorScheme="orange" mb={4}>Not configured</Badge>}
      <VStack spacing={4} align="stretch">
        <HStack spacing={4}>
          <FormControl>
            <FormLabel fontSize="sm">Bucket Name</FormLabel>
            <Input size="sm" value={form.bucket_name}
              onChange={e => setForm({ ...form, bucket_name: e.target.value })} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Region</FormLabel>
            <Input size="sm" value={form.region}
              onChange={e => setForm({ ...form, region: e.target.value })} />
          </FormControl>
        </HStack>
        <HStack spacing={4}>
          <FormControl>
            <FormLabel fontSize="sm">Access Key ID</FormLabel>
            <Input size="sm" value={form.access_key_id}
              onChange={e => setForm({ ...form, access_key_id: e.target.value })} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Secret Access Key</FormLabel>
            <Input size="sm" type="password"
              placeholder={config?.secret_access_key_set ? '••••••••' : ''}
              value={form.secret_access_key}
              onChange={e => setForm({ ...form, secret_access_key: e.target.value })} />
          </FormControl>
        </HStack>
        <HStack spacing={4}>
          <FormControl>
            <FormLabel fontSize="sm">Key Prefix</FormLabel>
            <Input size="sm" value={form.prefix}
              onChange={e => setForm({ ...form, prefix: e.target.value })} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Custom Endpoint URL (optional)</FormLabel>
            <Input size="sm" placeholder="https://s3.example.com"
              value={form.endpoint_url}
              onChange={e => setForm({ ...form, endpoint_url: e.target.value })} />
          </FormControl>
        </HStack>
        <HStack justifyContent="space-between" w="100%">
          <Button leftIcon={showTemplate ? <FiChevronUp /> : <FiChevronDown />}
            variant="outline" size="sm"
            onClick={() => setShowTemplate(!showTemplate)}>
            {showTemplate ? 'Hide' : 'Show'} CloudFormation Template
          </Button>
          <Button leftIcon={<FiSave />} colorScheme="blue" onClick={saveConfig}
            isLoading={saving}>
            Save S3 Configuration
          </Button>
        </HStack>
        {showTemplate && (
          <Box borderWidth="1px" borderRadius="md" borderColor="gray.200" overflow="hidden">
            <HStack justify="space-between" bg="gray.50" px={3} py={2}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                selwyn-backup-bucket.yaml — S3 bucket with 30-day lifecycle policy
              </Text>
              <Button size="xs" leftIcon={<FiCopy />} variant="ghost"
                onClick={() => {
                  navigator.clipboard.writeText(CLOUDFORMATION_TEMPLATE);
                  toast({ title: 'Copied to clipboard', status: 'success', duration: 2000 });
                }}>
                Copy
              </Button>
            </HStack>
            <Box as="pre" p={4} bg="gray.900" color="green.200" fontSize="xs"
              overflowX="auto" whiteSpace="pre" maxH="400px" overflowY="auto">
              {CLOUDFORMATION_TEMPLATE}
            </Box>
          </Box>
        )}
      </VStack>
    </Box>
  );
}

/* ────────────────────────────────
   Targets Panel
   ──────────────────────────────── */
function TargetsPanel() {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDiscoverOpen, onOpen: onDiscoverOpen, onClose: onDiscoverClose } = useDisclosure();
  const [editItem, setEditItem] = useState(null);
  const [discovered, setDiscovered] = useState([]);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverError, setDiscoverError] = useState(null);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const emptyForm = {
    name: '', target_type: 'postgres',
    pod_label_selector: '', container_name: '', namespace: '',
    host: 'localhost', port: 5432,
    username: '', password: '', database_name: '',
    minio_data_path: '/data', minio_bucket: '',
    use_ssl: false, is_enabled: true,
  };
  const [form, setForm] = useState({ ...emptyForm });
  const toast = useToast();

  const fetchTargets = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/backup-targets/`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => { setTargets(d.results || d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);

  const runDiscover = async () => {
    setDiscoverLoading(true);
    setDiscoverError(null);
    setDiscovered([]);
    setSelected({});
    try {
      const res = await fetch(`${API_BASE}/backups/discover/`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDiscovered(data.targets || []);
      // Pre-select new targets (not already existing)
      const sel = {};
      (data.targets || []).forEach((t, i) => { if (!t.already_exists) sel[i] = true; });
      setSelected(sel);
      onDiscoverOpen();
    } catch (err) {
      setDiscoverError(err.message);
      onDiscoverOpen();
    } finally {
      setDiscoverLoading(false);
    }
  };

  const importSelected = async () => {
    setImporting(true);
    let created = 0;
    let failed = 0;
    for (const [idx, checked] of Object.entries(selected)) {
      if (!checked) continue;
      const t = discovered[idx];
      const body = {
        name: t.name,
        target_type: t.target_type,
        pod_label_selector: t.pod_label_selector,
        container_name: t.container_name || '',
        namespace: t.namespace || '',
        host: t.host || 'localhost',
        port: t.port || (t.target_type === 'postgres' ? 5432 : 9000),
        minio_data_path: t.minio_data_path || '/data',
        minio_bucket: '',
        username: t.username || '',
        password: t.password || '',
        database_name: t.database_name || '',
        use_ssl: false,
        is_enabled: true,
      };
      try {
        const res = await fetch(`${API_BASE}/backup-targets/`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error();
        created++;
      } catch {
        failed++;
      }
    }
    setImporting(false);
    onDiscoverClose();
    toast({
      title: `Imported ${created} target(s)` + (failed ? `, ${failed} failed` : ''),
      status: failed ? 'warning' : 'success',
      duration: 4000,
    });
    fetchTargets();
  };

  const openCreate = () => { setEditItem(null); setForm({ ...emptyForm }); onOpen(); };
  const openEdit = (t) => {
    setEditItem(t);
    setForm({
      name: t.name, target_type: t.target_type,
      pod_label_selector: t.pod_label_selector || '',
      container_name: t.container_name || '',
      namespace: t.namespace || '',
      host: t.host, port: t.port,
      username: t.username, password: '', database_name: t.database_name,
      minio_data_path: t.minio_data_path || '/data',
      minio_bucket: t.minio_bucket || '',
      use_ssl: t.use_ssl, is_enabled: t.is_enabled,
    });
    onOpen();
  };

  const saveTarget = async () => {
    const body = { ...form, port: parseInt(form.port, 10) || 5432 };
    if (!body.password && editItem) delete body.password;
    const url = editItem ? `${API_BASE}/backup-targets/${editItem.id}/` : `${API_BASE}/backup-targets/`;
    try {
      const res = await fetch(url, {
        method: editItem ? 'PATCH' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: editItem ? 'Target updated' : 'Target created', status: 'success', duration: 3000 });
      onClose();
      fetchTargets();
    } catch (err) {
      toast({ title: 'Failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const deleteTarget = async (id, name) => {
    if (!window.confirm(`Delete target "${name}"?`)) return;
    try {
      await fetch(`${API_BASE}/backup-targets/${id}/`, { method: 'DELETE', headers: getAuthHeaders() });
      toast({ title: 'Target deleted', status: 'success', duration: 3000 });
      fetchTargets();
    } catch (err) {
      toast({ title: 'Failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const triggerBackup = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/backup-targets/${id}/backup_now/`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast({
        title: 'Backup started',
        description: `Backup #${data.id} is running in the background. Check Backup History for progress.`,
        status: 'info',
        duration: 6000,
      });
      fetchTargets();
    } catch (err) {
      toast({ title: 'Backup failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  if (loading) return <Spinner />;

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Text fontWeight="semibold">{targets.length} backup target(s)</Text>
        <HStack>
          <Button size="sm" leftIcon={<FiSearch />} colorScheme="teal" onClick={runDiscover}
            isLoading={discoverLoading} loadingText="Scanning…">Discover Targets</Button>
          <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate}>Add Target</Button>
          <Button size="sm" leftIcon={<FiRefreshCw />} onClick={fetchTargets}>Refresh</Button>
        </HStack>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Name</Th><Th>Type</Th><Th>Pod Selector</Th><Th>Enabled</Th><Th>Last Backup</Th><Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {targets.map(t => (
              <Tr key={t.id}>
                <Td fontWeight="medium">{t.name}</Td>
                <Td><Badge colorScheme={t.target_type === 'postgres' ? 'blue' : 'purple'}>{t.target_type}</Badge></Td>
                <Td fontSize="sm" maxW="200px" isTruncated title={t.pod_label_selector}>{t.pod_label_selector}</Td>
                <Td><Badge colorScheme={t.is_enabled ? 'green' : 'gray'}>{t.is_enabled ? 'Yes' : 'No'}</Badge></Td>
                <Td fontSize="xs">{t.last_backup ? `${t.last_backup.status} – ${new Date(t.last_backup.created_at).toLocaleString()}` : 'Never'}</Td>
                <Td>
                  <HStack spacing={1}>
                    <Tooltip label="Backup now">
                      <IconButton size="xs" icon={<FiPlay />} colorScheme="green" onClick={() => triggerBackup(t.id)} />
                    </Tooltip>
                    <Tooltip label="Edit">
                      <IconButton size="xs" icon={<FiEdit2 />} onClick={() => openEdit(t)} />
                    </Tooltip>
                    <Tooltip label="Delete">
                      <IconButton size="xs" icon={<FiTrash2 />} colorScheme="red" variant="ghost" onClick={() => deleteTarget(t.id, t.name)} />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
            {targets.length === 0 && (
              <Tr><Td colSpan={6} textAlign="center" py={6} color="gray.400">No backup targets configured</Td></Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Discover Targets Modal */}
      <Modal isOpen={isDiscoverOpen} onClose={onDiscoverClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Discover Backup Targets</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {discoverError && (
              <Alert status="error" mb={4} borderRadius="md">
                <AlertIcon />
                <AlertDescription>{discoverError}</AlertDescription>
              </Alert>
            )}
            {discovered.length === 0 && !discoverError && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription>No Postgres or MinIO pods found in the cluster.</AlertDescription>
              </Alert>
            )}
            {discovered.length > 0 && (
              <VStack spacing={0} align="stretch">
                <Text fontSize="sm" color="gray.500" mb={3}>
                  Found {discovered.length} potential target(s). Select the ones you'd like to import.
                </Text>
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th w="40px"></Th>
                      <Th>Name</Th>
                      <Th>Type</Th>
                      <Th>Pod Selector</Th>
                      <Th>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {discovered.map((t, i) => (
                      <Tr key={i} opacity={t.already_exists ? 0.5 : 1}>
                        <Td>
                          <Checkbox
                            isChecked={!!selected[i]}
                            isDisabled={t.already_exists}
                            onChange={e => setSelected({ ...selected, [i]: e.target.checked })}
                          />
                        </Td>
                        <Td fontWeight="medium" fontSize="sm">{t.name}</Td>
                        <Td>
                          <Badge colorScheme={t.target_type === 'postgres' ? 'blue' : 'purple'} fontSize="xs">
                            {t.target_type}
                          </Badge>
                        </Td>
                        <Td fontSize="xs" maxW="200px" isTruncated title={t.pod_label_selector}>
                          {t.pod_label_selector}
                        </Td>
                        <Td>
                          {t.already_exists
                            ? <Badge colorScheme="gray" fontSize="xs">Already configured</Badge>
                            : <Badge colorScheme="green" fontSize="xs">New</Badge>}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDiscoverClose}>Cancel</Button>
            <Button
              colorScheme="blue"
              leftIcon={<FiDownload />}
              onClick={importSelected}
              isLoading={importing}
              isDisabled={!Object.values(selected).some(Boolean)}
            >
              Import Selected
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create / Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editItem ? 'Edit Target' : 'Add Backup Target'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <HStack w="100%" spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Name</FormLabel>
                  <Input size="sm" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Type</FormLabel>
                  <Select size="sm" value={form.target_type} onChange={e => setForm({ ...form, target_type: e.target.value })}>
                    <option value="postgres">PostgreSQL</option>
                    <option value="minio">MinIO</option>
                  </Select>
                </FormControl>
              </HStack>

              <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" w="100%" mt={2}>
                Kubernetes Pod Targeting
              </Text>
              <FormControl>
                <FormLabel fontSize="sm">Pod Label Selector</FormLabel>
                <Input size="sm" placeholder="app=selwyn-postgres"
                  value={form.pod_label_selector} onChange={e => setForm({ ...form, pod_label_selector: e.target.value })} />
                <Text fontSize="xs" color="gray.400" mt={1}>Used to find a running pod to exec into</Text>
              </FormControl>
              <HStack w="100%" spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Container Name (optional)</FormLabel>
                  <Input size="sm" placeholder="Leave blank for default container"
                    value={form.container_name} onChange={e => setForm({ ...form, container_name: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Namespace (optional)</FormLabel>
                  <Input size="sm" placeholder="selwyn (default)"
                    value={form.namespace} onChange={e => setForm({ ...form, namespace: e.target.value })} />
                </FormControl>
              </HStack>

              <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" w="100%" mt={2}>
                Connection Details (inside the pod)
              </Text>
              <HStack w="100%" spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Host</FormLabel>
                  <Input size="sm" value={form.host} onChange={e => setForm({ ...form, host: e.target.value })} />
                  <Text fontSize="xs" color="gray.400" mt={1}>Usually localhost when exec'ing into the DB pod</Text>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Port</FormLabel>
                  <Input size="sm" type="number" value={form.port} onChange={e => setForm({ ...form, port: e.target.value })} />
                </FormControl>
              </HStack>

              {form.target_type === 'postgres' && (
                <>
                  <HStack w="100%" spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Username</FormLabel>
                      <Input size="sm" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Password</FormLabel>
                      <Input size="sm" type="password"
                        placeholder={editItem?.password_set ? '••••••••' : ''}
                        value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                    </FormControl>
                  </HStack>
                  <FormControl>
                    <FormLabel fontSize="sm">Database Name</FormLabel>
                    <Input size="sm" value={form.database_name} onChange={e => setForm({ ...form, database_name: e.target.value })} />
                  </FormControl>
                </>
              )}

              {form.target_type === 'minio' && (
                <>
                  <FormControl>
                    <FormLabel fontSize="sm">Data Path</FormLabel>
                    <Input size="sm" value={form.minio_data_path}
                      onChange={e => setForm({ ...form, minio_data_path: e.target.value })} />
                    <Text fontSize="xs" color="gray.400" mt={1}>MinIO data directory inside the container (default: /data)</Text>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Bucket (optional)</FormLabel>
                    <Input size="sm" placeholder="Leave blank to back up all buckets"
                      value={form.minio_bucket}
                      onChange={e => setForm({ ...form, minio_bucket: e.target.value })} />
                  </FormControl>
                </>
              )}

              <HStack w="100%" spacing={8}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>Enabled</FormLabel>
                  <Switch isChecked={form.is_enabled} onChange={e => setForm({ ...form, is_enabled: e.target.checked })} />
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={saveTarget}>{editItem ? 'Update' : 'Create'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

/* ────────────────────────────────
   Schedules Panel
   ──────────────────────────────── */
function SchedulesPanel() {
  const [schedules, setSchedules] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editItem, setEditItem] = useState(null);
  const emptyForm = { target: '', interval: 'daily', is_enabled: true };
  const [form, setForm] = useState({ ...emptyForm });
  const toast = useToast();

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/backup-schedules/`, { headers: getAuthHeaders() }).then(r => r.json()),
      fetch(`${API_BASE}/backup-targets/`, { headers: getAuthHeaders() }).then(r => r.json()),
    ]).then(([sd, td]) => {
      setSchedules(sd.results || sd);
      setTargets(td.results || td);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditItem(null); setForm({ ...emptyForm }); onOpen(); };
  const openEdit = (s) => {
    setEditItem(s);
    setForm({ target: s.target, interval: s.interval, is_enabled: s.is_enabled });
    onOpen();
  };

  const save = async () => {
    const url = editItem ? `${API_BASE}/backup-schedules/${editItem.id}/` : `${API_BASE}/backup-schedules/`;
    try {
      const res = await fetch(url, {
        method: editItem ? 'PATCH' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: editItem ? 'Schedule updated' : 'Schedule created', status: 'success', duration: 3000 });
      onClose();
      fetchData();
    } catch (err) {
      toast({ title: 'Failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const deleteSched = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    await fetch(`${API_BASE}/backup-schedules/${id}/`, { method: 'DELETE', headers: getAuthHeaders() });
    fetchData();
  };

  if (loading) return <Spinner />;

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Text fontWeight="semibold">{schedules.length} schedule(s)</Text>
        <HStack>
          <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate}>Add Schedule</Button>
          <Button size="sm" leftIcon={<FiRefreshCw />} onClick={fetchData}>Refresh</Button>
        </HStack>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Target</Th><Th>Type</Th><Th>Interval</Th><Th>Enabled</Th><Th>Next Run</Th><Th>Last Run</Th><Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {schedules.map(s => (
              <Tr key={s.id}>
                <Td fontWeight="medium">{s.target_name}</Td>
                <Td><Badge colorScheme={s.target_type === 'postgres' ? 'blue' : 'purple'}>{s.target_type}</Badge></Td>
                <Td>{s.interval}</Td>
                <Td><Badge colorScheme={s.is_enabled ? 'green' : 'gray'}>{s.is_enabled ? 'Yes' : 'No'}</Badge></Td>
                <Td fontSize="xs">{new Date(s.next_run).toLocaleString()}</Td>
                <Td fontSize="xs">{s.last_run ? new Date(s.last_run).toLocaleString() : 'Never'}</Td>
                <Td>
                  <HStack spacing={1}>
                    <IconButton size="xs" icon={<FiEdit2 />} onClick={() => openEdit(s)} />
                    <IconButton size="xs" icon={<FiTrash2 />} colorScheme="red" variant="ghost" onClick={() => deleteSched(s.id)} />
                  </HStack>
                </Td>
              </Tr>
            ))}
            {schedules.length === 0 && (
              <Tr><Td colSpan={7} textAlign="center" py={6} color="gray.400">No schedules configured</Td></Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editItem ? 'Edit Schedule' : 'Add Schedule'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Target</FormLabel>
                <Select size="sm" value={form.target}
                  onChange={e => setForm({ ...form, target: e.target.value })}>
                  <option value="">Select a target…</option>
                  {targets.map(t => <option key={t.id} value={t.id}>{t.name} ({t.target_type})</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Interval</FormLabel>
                <Select size="sm" value={form.interval}
                  onChange={e => setForm({ ...form, interval: e.target.value })}>
                  <option value="hourly">Every hour</option>
                  <option value="every_6h">Every 6 hours</option>
                  <option value="every_12h">Every 12 hours</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </Select>
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>Enabled</FormLabel>
                <Switch isChecked={form.is_enabled} onChange={e => setForm({ ...form, is_enabled: e.target.checked })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={save}>{editItem ? 'Update' : 'Create'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

/* ────────────────────────────────
   Main Backup Config Page
   ──────────────────────────────── */
export default function BackupConfig() {
  return (
    <Box>
      <VStack align="start" spacing={1} mb={6}>
        <Heading size="lg">Backup Configuration</Heading>
        <Text color="gray.500">Configure S3 storage, backup targets, and schedules</Text>
      </VStack>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>S3 Storage</Tab>
          <Tab>Targets</Tab>
          <Tab>Schedules</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}><S3ConfigPanel /></TabPanel>
          <TabPanel px={0}><TargetsPanel /></TabPanel>
          <TabPanel px={0}><SchedulesPanel /></TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
